/**
 * Whether the watch ran, as one chip in the Monitor's header — the way Linear
 * shows a sync, not a page section. Its popover holds the monitoring switch,
 * each source (health, last read, watch toggle), the sources the firm's
 * regimes call for, and adding one.
 *
 * @module components/regulation/monitor/SourcesStatus
 */
import { EyeIcon, EyeOffIcon, PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "~/lib/utils";
import { useMonitors, type Monitor } from "../../../state/governance";
import { useOrgProfile } from "../../../state/orgProfile";
import { useRegulatoryUpdates } from "../../../state/policies";
import { Button } from "../../ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "../../ui/popover";
import { RegulatoryMonitoringToggle } from "../RegulatoryMonitoringToggle";
import { useRegulationOrg } from "../useRegulationOrg";
import { AddMonitorDialog } from "./AddMonitorDialog";
import { isQuiet, relative } from "./publications";

/** The one-line verdict on the watch: how many sources, and whether any has gone quiet. */
export function sourcesVerdict(
  monitors: ReadonlyArray<Pick<Monitor, "enabled" | "lastCheckedAt">>,
  now: number,
): { readonly tone: "good" | "warn" | "off"; readonly quiet: number; readonly watched: number } {
  const watched = monitors.filter((monitor) => monitor.enabled);
  const quiet = watched.filter((monitor) => isQuiet(monitor, now)).length;
  return { tone: watched.length === 0 ? "off" : quiet > 0 ? "warn" : "good", quiet, watched: watched.length };
}

const DOT = { good: "bg-success", warn: "bg-warning", off: "bg-muted-foreground/40" } as const;

function SourceRow({
  monitor,
  now,
  canManage,
  busy,
  onToggle,
}: {
  readonly monitor: Monitor;
  readonly now: number;
  readonly canManage: boolean;
  readonly busy: boolean;
  readonly onToggle: () => void;
}) {
  const quiet = isQuiet(monitor, now);
  return (
    <li
      title={monitor.lastCheckNote ?? undefined}
      className={cn("flex h-9 items-center gap-2.5 rounded-md px-2 text-[13px]", !monitor.enabled && "opacity-60")}
    >
      <span
        aria-hidden
        className={cn(
          "size-1.5 shrink-0 rounded-full",
          !monitor.enabled ? "bg-muted-foreground/40" : quiet ? "bg-warning" : "bg-success",
        )}
      />
      <span className="min-w-0 flex-1 truncate font-[510] text-foreground/90">{monitor.issuer.split(" — ")[0]}</span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {monitor.enabled ? `read ${relative(monitor.lastCheckedAt, now)}` : "off"}
      </span>
      {canManage ? (
        <button
          type="button"
          disabled={busy}
          onClick={onToggle}
          aria-label={monitor.enabled ? `Stop watching ${monitor.issuer}` : `Watch ${monitor.issuer} again`}
          title={monitor.enabled ? "Stop watching" : "Watch again"}
          className="flex size-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
        >
          {monitor.enabled ? <EyeOffIcon className="size-3.5" /> : <EyeIcon className="size-3.5" />}
        </button>
      ) : null}
    </li>
  );
}

export function SourcesStatus() {
  const { org } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const { canManage, profile } = useOrgProfile(organizationId);
  const monitors = useMonitors(organizationId);
  const updates = useRegulatoryUpdates(organizationId);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const now = useMemo(() => Date.now(), [monitors.snapshot, updates.snapshot]);
  if (org === null || monitors.unavailableReason !== null || monitors.snapshot === null) return null;

  const watched = monitors.snapshot.monitors;
  const suggested = monitors.snapshot.suggested;
  const verdict = sourcesVerdict(watched, now);
  // Monitoring switched off outranks any source's health: nothing is read at all.
  const off = profile?.regulatoryMonitoringEnabled === false;
  const lastRead = updates.snapshot?.lastCheckedAt ?? null;
  const run = async (action: () => Promise<string | null>) => {
    setBusy(true);
    setMessage(null);
    const error = await action();
    setBusy(false);
    setMessage(error);
    return error;
  };

  return (
    <>
      <AddMonitorDialog
        organizationId={organizationId}
        open={adding}
        onOpenChange={setAdding}
        onCreate={(input) => run(() => monitors.create(input))}
      />
      <Popover>
        <PopoverTrigger
          render={
            <button
              type="button"
              className="inline-flex h-7 cursor-pointer items-center gap-2 rounded-md border border-border/70 px-2.5 text-xs text-muted-foreground outline-hidden transition-colors hover:bg-foreground/[0.04] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            />
          }
        >
          <span aria-hidden className={cn("size-1.5 rounded-full", DOT[off ? "off" : verdict.tone])} />
          <span className="font-medium text-foreground/85">
            {off ? "Monitoring off" : `${verdict.watched} ${verdict.watched === 1 ? "source" : "sources"}`}
          </span>
          <span className={off ? "hidden" : undefined}>
            {verdict.tone === "off"
              ? "none watched"
              : verdict.quiet > 0
                ? `${verdict.quiet} quiet`
                : lastRead
                  ? `read ${relative(lastRead, now)}`
                  : "never read"}
          </span>
        </PopoverTrigger>
        <PopoverPopup align="end" className="w-[26rem] p-0">
          <div className="border-b border-border/60 px-3 py-2.5">
            <RegulatoryMonitoringToggle compact className="w-full justify-between text-[13px] text-foreground" />
          </div>
          <div className="flex items-center justify-between px-3 pt-3 pb-1">
            <p className="text-xs font-[510] text-muted-foreground">Sources</p>
            {canManage ? (
              <Button
                type="button"
                size="xs"
                variant="ghost"
                onClick={() => setAdding(true)}
                className="h-6 cursor-pointer gap-1 text-xs"
              >
                <PlusIcon className="size-3.5" />
                Add
              </Button>
            ) : null}
          </div>
          <ul className="px-1 pb-1">
            {watched.map((monitor) => (
              <SourceRow
                key={monitor.id}
                monitor={monitor}
                now={now}
                canManage={canManage}
                busy={busy}
                onToggle={() => void run(() => monitors.patch(monitor.id, { enabled: !monitor.enabled }))}
              />
            ))}
            {watched.length === 0 ? (
              <li className="px-2 py-3 text-xs text-muted-foreground">
                Enable a regime and the sources its issuer publishes on appear here.
              </li>
            ) : null}
          </ul>
          {suggested.length > 0 ? (
            <div className="border-t border-border/60 px-1 py-1">
              <p className="px-2 pt-1.5 pb-1 text-xs font-[510] text-muted-foreground">Your regimes call for</p>
              {suggested.map((suggestion) => (
                <button
                  key={suggestion.catalogueId}
                  type="button"
                  disabled={!canManage || busy}
                  onClick={() => void run(() => monitors.create({ catalogueId: suggestion.catalogueId }))}
                  title={suggestion.frameworks.map((framework) => framework.name).join(", ")}
                  className="flex h-9 w-full cursor-pointer items-center gap-2.5 rounded-md px-2 text-left text-[13px] text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground disabled:cursor-default"
                >
                  <EyeIcon className="size-3.5 shrink-0" />
                  Watch {suggestion.issuer.split(" — ")[0]}
                </button>
              ))}
            </div>
          ) : null}
          <p className="border-t border-border/60 px-3 py-2 text-xs text-muted-foreground">
            A source goes quiet when it has not been read for two weeks.
          </p>
          {message !== null ? <p className="px-3 pb-2 text-xs text-destructive">{message}</p> : null}
        </PopoverPopup>
      </Popover>
    </>
  );
}
